export interface RescheduleDateRange {
    startDate: string;
    endDate: string;
    startDateLocalDate?: string;
    endDateLocalDate?: string;
}

export interface RescheduleDateChange {
    previousStartDate: string;
    previousEndDate: string;
    newStartDate: string;
    newEndDate: string;
    previousStartDateLocalDate?: string;
    previousEndDateLocalDate?: string;
    newStartDateLocalDate?: string;
    newEndDateLocalDate?: string;
}

const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const dateKey = (value: string, localDate?: string): string => {
    const match = (localDate ?? value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (!match) {
        throw new RangeError('A valid calendar date is required.');
    }
    return match[1];
};

const dateParts = (key: string) => {
    const [year, month, day] = key.split('-').map(Number);
    return { year, month, day };
};

const fullDate = ({ year, month, day }: ReturnType<typeof dateParts>): string =>
    `${day} ${months[month - 1]} ${year}`;

export const formatRescheduleDateRange = ({
    startDate,
    endDate,
    startDateLocalDate,
    endDateLocalDate,
}: RescheduleDateRange): string => {
    const startKey = dateKey(startDate, startDateLocalDate);
    const endKey = dateKey(endDate, endDateLocalDate);
    const start = dateParts(startKey);
    const end = dateParts(endKey);

    if (startKey === endKey) {
        return fullDate(start);
    }
    if (start.year === end.year && start.month === end.month) {
        return `${start.day}–${end.day} ${months[start.month - 1]} ${start.year}`;
    }
    if (start.year === end.year) {
        return `${start.day} ${months[start.month - 1]} – ${end.day} ${months[end.month - 1]} ${end.year}`;
    }
    return `${fullDate(start)} – ${fullDate(end)}`;
};

export const hasRescheduleDateChange = (change: RescheduleDateChange): boolean =>
    dateKey(change.previousStartDate, change.previousStartDateLocalDate) !==
        dateKey(change.newStartDate, change.newStartDateLocalDate) ||
    dateKey(change.previousEndDate, change.previousEndDateLocalDate) !==
        dateKey(change.newEndDate, change.newEndDateLocalDate);

export const formatRescheduleDateChange = (change: RescheduleDateChange): string => {
    if (!hasRescheduleDateChange(change)) {
        return 'Your event schedule has changed. Check the schedule for the latest session times.';
    }

    const previousRange = formatRescheduleDateRange({
        startDate: change.previousStartDate,
        endDate: change.previousEndDate,
        startDateLocalDate: change.previousStartDateLocalDate,
        endDateLocalDate: change.previousEndDateLocalDate,
    });
    const newRange = formatRescheduleDateRange({
        startDate: change.newStartDate,
        endDate: change.newEndDate,
        startDateLocalDate: change.newStartDateLocalDate,
        endDateLocalDate: change.newEndDateLocalDate,
    });
    return `Previously ${previousRange} · now ${newRange}. Check the schedule for session times.`;
};
